import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Plus, Edit, Trash2, Loader2, AlertCircle, Search, ChevronDown, Package, RefreshCw, X, Zap } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';

const API   = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LIMIT = 10;

function useDebounce(value, delay = 400) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

const CATEGORY_CONFIG = {
  car:        { label: 'Car',        pill: 'bg-blue-100 text-blue-700 border-blue-200',      dot: 'bg-blue-500',   badge: 'bg-blue-50 text-blue-600' },
  bike:       { label: 'Bike',       pill: 'bg-orange-100 text-orange-700 border-orange-200', dot: 'bg-orange-500', badge: 'bg-orange-50 text-orange-600' },
  ev:         { label: 'EV',         pill: 'bg-green-100 text-green-700 border-green-200',    dot: 'bg-green-500',  badge: 'bg-green-50 text-green-600' },
  commercial: { label: 'Commercial', pill: 'bg-purple-100 text-purple-700 border-purple-200', dot: 'bg-purple-500', badge: 'bg-purple-50 text-purple-600' },
};

const EV_SUBTYPES = [
  { value: 'car',        label: 'EV Car',        icon: '🚗', desc: 'Electric cars & sedans' },
  { value: 'bike',       label: 'EV Bike',       icon: '🏍️', desc: 'Electric bikes & scooters' },
  { value: 'commercial', label: 'EV Commercial', icon: '🚛', desc: 'Electric trucks & vans' },
];

const ModelsTab = ({ brands, onDataChange }) => {
  const { token } = useAuth();
  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };

  const [activeCategory, setActiveCategory] = useState('car');
  const [allModels, setAllModels]           = useState([]);
  const [displayModels, setDisplayModels]   = useState([]);
  const [displayTotal, setDisplayTotal]     = useState(0);
  const [displayHasMore, setDisplayHasMore] = useState(false);
  const [displaySkip, setDisplaySkip]       = useState(0);
  const [loading, setLoading]               = useState(false);
  const [searchQuery, setSearchQuery]       = useState('');
  const debouncedSearch                     = useDebounce(searchQuery, 350);

  const [showDialog, setShowDialog]         = useState(false);
  const [editingId, setEditingId]           = useState(null);
  // ev_subtype added to form state
  const [modelForm, setModelForm]           = useState({ name: '', brand_id: '', ev_subtype: '' });
  const [saving, setSaving]                 = useState(false);
  const [brandSearch, setBrandSearch]       = useState('');

  const sentinelRef = useRef(null);

  const getBrand = useCallback((id) => brands.find(b => b.id === id), [brands]);

  const fmtDate = (iso) =>
    iso ? new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

  const categoryBrandIds = useMemo(
    () => new Set(brands.filter(b => b.category === activeCategory).map(b => b.id)),
    [brands, activeCategory]
  );

  const isEV = activeCategory === 'ev';

  const fetchAllCategoryModels = useCallback(async () => {
    if (categoryBrandIds.size === 0) { setAllModels([]); return; }
    setLoading(true);
    try {
      let collected = [];
      let skip = 0;
      const pageSize = 50;
      while (true) {
        const params = new URLSearchParams({ skip, limit: pageSize });
        const res = await axios.get(`${API}/models?${params}`, authHeaders);
        const page = res.data.models || [];
        const filtered = page.filter(m => categoryBrandIds.has(m.brand_id));
        collected = [...collected, ...filtered];
        if (!res.data.has_more) break;
        skip += pageSize;
      }
      collected.sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''));
      setAllModels(collected);
    } catch {
      toast.error('Failed to load models');
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCategory, categoryBrandIds]);

  useEffect(() => {
    setAllModels([]);
    setDisplayModels([]);
    setDisplaySkip(0);
    setSearchQuery('');
    fetchAllCategoryModels();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCategory]);

  useEffect(() => {
    if (brands.length > 0) fetchAllCategoryModels();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brands]);

  const filteredModels = useMemo(() => {
    const q = debouncedSearch.trim().toLowerCase();
    if (!q) return allModels;
    return allModels.filter(m => {
      const brand = getBrand(m.brand_id);
      return m.name?.toLowerCase().includes(q) || brand?.name?.toLowerCase().includes(q);
    });
  }, [allModels, debouncedSearch, getBrand]);

  const applyPage = useCallback((filtered, skip, append) => {
    const page = filtered.slice(skip, skip + LIMIT);
    setDisplayModels(prev => append ? [...prev, ...page] : page);
    setDisplayTotal(filtered.length);
    setDisplayHasMore((skip + LIMIT) < filtered.length);
    setDisplaySkip(skip + LIMIT);
  }, []);

  useEffect(() => {
    applyPage(filteredModels, 0, false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredModels]);

  useEffect(() => {
    if (!sentinelRef.current) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && displayHasMore && !loading) applyPage(filteredModels, displaySkip, true);
      },
      { threshold: 0.1 }
    );
    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [displayHasMore, loading, displaySkip, filteredModels, applyPage]);

  const groupedByBrand = useMemo(() => {
    const map = {};
    displayModels.forEach(m => {
      if (!map[m.brand_id]) map[m.brand_id] = [];
      map[m.brand_id].push(m);
    });
    return Object.entries(map).sort(([, a], [, b]) =>
      (b[0]?.created_at || '').localeCompare(a[0]?.created_at || '')
    );
  }, [displayModels]);

  const handleSave = async () => {
    if (!modelForm.name.trim() || !modelForm.brand_id) {
      toast.error('Please fill all required fields');
      return;
    }
    // EV subtype is mandatory when category is EV
    if (isEV && !modelForm.ev_subtype) {
      toast.error('Please select the EV subtype (Car, Bike, or Commercial)');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: modelForm.name.trim(),
        brand_id: modelForm.brand_id,
        ev_subtype: isEV ? modelForm.ev_subtype : null,
      };
      if (editingId) {
        await axios.put(`${API}/superadmin/models/${editingId}`, payload, authHeaders);
        toast.success('Model updated');
      } else {
        await axios.post(`${API}/superadmin/models`, payload, authHeaders);
        toast.success('Model created');
      }
      setShowDialog(false);
      resetForm();
      await fetchAllCategoryModels();
      if (onDataChange) onDataChange();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save model');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (modelId) => {
    if (!window.confirm('Delete this model? All associated variants will also be deleted.')) return;
    try {
      await axios.delete(`${API}/superadmin/models/${modelId}`, authHeaders);
      toast.success('Model deleted');
      setAllModels(prev => prev.filter(m => m.id !== modelId));
      if (onDataChange) onDataChange();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete model');
    }
  };

  const openEdit = (model) => {
    setModelForm({ name: model.name, brand_id: model.brand_id, ev_subtype: model.ev_subtype || '' });
    setEditingId(model.id);
    setShowDialog(true);
  };

  const resetForm = () => {
    setModelForm({ name: '', brand_id: '', ev_subtype: '' });
    setEditingId(null);
    setBrandSearch('');
  };

  const dialogBrands = useMemo(() => {
    return brands.filter(b => {
      const matchCat    = b.category === activeCategory;
      const matchSearch = !brandSearch.trim() || b.name?.toLowerCase().includes(brandSearch.trim().toLowerCase());
      return matchCat && matchSearch;
    });
  }, [brands, activeCategory, brandSearch]);

  const catCfg = CATEGORY_CONFIG[activeCategory];

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-col gap-4">

          {/* Row 1: title + refresh + add */}
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Package className="h-5 w-5 text-blue-600" />
                Model Management
                {!loading && (
                  <span className="text-sm font-normal text-gray-400 ml-1">
                    ({displayModels.length} of {displayTotal} shown · {allModels.length} in {catCfg.label})
                  </span>
                )}
              </CardTitle>
              <p className="text-sm text-gray-500 mt-1">Grouped by brand · newest first · 10 per page</p>
            </div>

            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm"
                onClick={() => { setAllModels([]); fetchAllCategoryModels(); }}
                disabled={loading}>
                <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              </Button>

              <Dialog open={showDialog} onOpenChange={open => { if (!open) resetForm(); setShowDialog(open); }}>
                <DialogTrigger asChild>
                  <Button className="bg-blue-600 hover:bg-blue-700 text-white" onClick={resetForm} data-testid="add-model-btn">
                    <Plus className="mr-2 h-4 w-4" /> Add Model
                  </Button>
                </DialogTrigger>

                <DialogContent className="max-w-md">
                  <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                      {editingId ? 'Edit Model' : 'Add New Model'}
                      {isEV && (
                        <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-medium flex items-center gap-1">
                          <Zap className="h-3 w-3" /> EV
                        </span>
                      )}
                    </DialogTitle>
                  </DialogHeader>

                  <div className="space-y-4">

                    {/* ── EV SUBTYPE SELECTOR — only shown when category is EV ── */}
                    {isEV && (
                      <div>
                        <Label className="mb-1.5 block">
                          EV Subtype *
                          <span className="ml-2 text-xs text-gray-400 font-normal">
                            Determines which buyers see this model
                          </span>
                        </Label>
                        <div className="grid grid-cols-3 gap-2">
                          {EV_SUBTYPES.map(sub => {
                            const isSelected = modelForm.ev_subtype === sub.value;
                            return (
                              <button
                                key={sub.value}
                                type="button"
                                onClick={() => setModelForm(f => ({ ...f, ev_subtype: sub.value }))}
                                className={`flex flex-col items-center gap-1.5 p-3 rounded-xl border-2 text-center transition-all ${
                                  isSelected
                                    ? 'border-green-500 bg-green-50 text-green-700 shadow-sm'
                                    : 'border-gray-200 bg-gray-50 text-gray-600 hover:border-green-300 hover:bg-green-50/50'
                                }`}
                              >
                                <span className="text-xl">{sub.icon}</span>
                                <span className="text-xs font-semibold leading-tight">{sub.label}</span>
                                <span className="text-[10px] text-gray-400 leading-tight">{sub.desc}</span>
                                {isSelected && (
                                  <span className="text-xs bg-green-500 text-white px-1.5 py-0.5 rounded font-bold">✓</span>
                                )}
                              </button>
                            );
                          })}
                        </div>
                        {!modelForm.ev_subtype && (
                          <p className="text-xs text-amber-600 mt-1.5">
                            ⚠️ Select a subtype so sellers see this model in the correct EV category.
                          </p>
                        )}
                        {modelForm.ev_subtype && (
                          <p className="text-xs text-green-600 mt-1.5">
                            ✓ Appears when sellers choose{' '}
                            <strong>EV → {EV_SUBTYPES.find(s => s.value === modelForm.ev_subtype)?.label}</strong>.
                          </p>
                        )}
                      </div>
                    )}

                    {/* Brand picker */}
                    <div>
                      <Label className="mb-1.5 block">
                        Brand *
                        <span className={`ml-2 text-xs px-2 py-0.5 rounded-full font-medium capitalize ${catCfg.badge}`}>
                          {catCfg.label}
                        </span>
                      </Label>
                      <div className="border border-gray-200 rounded-lg overflow-hidden focus-within:ring-2 focus-within:ring-blue-400 transition">
                        <div className="flex items-center gap-2 px-3 py-2 border-b border-gray-100 bg-gray-50">
                          <Search className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
                          <input
                            type="text"
                            value={brandSearch}
                            onChange={e => setBrandSearch(e.target.value)}
                            placeholder={`Search ${catCfg.label} brands...`}
                            className="flex-1 text-sm bg-transparent outline-none placeholder-gray-400 text-gray-700"
                            autoComplete="off"
                          />
                          {brandSearch && (
                            <button type="button" onClick={() => setBrandSearch('')} className="text-gray-400 hover:text-gray-600 text-xs">✕</button>
                          )}
                        </div>
                        <div className="max-h-44 overflow-y-auto">
                          {dialogBrands.length === 0 ? (
                            <p className="px-3 py-4 text-center text-sm text-gray-400">
                              {brandSearch ? `No brands match "${brandSearch}"` : `No ${catCfg.label} brands found`}
                            </p>
                          ) : dialogBrands.map(brand => {
                            const isSelected = modelForm.brand_id === brand.id;
                            return (
                              <button key={brand.id} type="button"
                                onClick={() => { setModelForm(f => ({ ...f, brand_id: brand.id })); setBrandSearch(''); }}
                                className={`w-full flex items-center gap-3 px-3 py-2.5 text-left text-sm transition-colors hover:bg-blue-50 ${isSelected ? 'bg-blue-50 text-blue-700' : 'text-gray-700'}`}>
                                {brand.logo ? (
                                  <img src={brand.logo} alt={brand.name} className="w-6 h-6 object-contain rounded border border-gray-200 bg-white p-0.5 flex-shrink-0" />
                                ) : (
                                  <div className="w-6 h-6 rounded bg-blue-100 flex items-center justify-center text-blue-600 text-xs font-bold flex-shrink-0">
                                    {brand.name?.charAt(0)}
                                  </div>
                                )}
                                <span className="flex-1 font-medium">{brand.name}</span>
                                <span className={`text-xs px-1.5 py-0.5 rounded capitalize font-medium ${catCfg.badge}`}>{brand.category}</span>
                                {isSelected && <span className="text-blue-600 text-xs font-semibold">✓</span>}
                              </button>
                            );
                          })}
                        </div>
                        {modelForm.brand_id && (() => {
                          const sel = getBrand(modelForm.brand_id);
                          return sel ? (
                            <div className="flex items-center gap-2 px-3 py-2 bg-blue-50 border-t border-blue-100">
                              {sel.logo ? <img src={sel.logo} alt={sel.name} className="w-5 h-5 object-contain rounded" /> : <div className="w-5 h-5 rounded bg-blue-200 flex items-center justify-center text-blue-700 text-xs font-bold">{sel.name?.charAt(0)}</div>}
                              <span className="text-xs text-blue-700 font-semibold">Selected: {sel.name}</span>
                            </div>
                          ) : null;
                        })()}
                      </div>
                    </div>

                    {/* Model name */}
                    <div>
                      <Label className="mb-1.5 block">Model Name *</Label>
                      <Input
                        value={modelForm.name}
                        onChange={e => setModelForm(f => ({ ...f, name: e.target.value }))}
                        placeholder={isEV ? 'e.g., Nexon EV' : 'e.g., Creta'}
                        data-testid="model-name-input"
                        onKeyDown={e => e.key === 'Enter' && handleSave()}
                      />
                    </div>

                    {/* EV summary card */}
                    {isEV && modelForm.ev_subtype && modelForm.brand_id && modelForm.name && (
                      <div className="bg-green-50 border border-green-200 rounded-xl px-4 py-3">
                        <p className="text-xs text-green-700 font-semibold mb-1">Summary</p>
                        <p className="text-xs text-green-600">
                          <strong>{modelForm.name}</strong> will appear under{' '}
                          <strong>{getBrand(modelForm.brand_id)?.name}</strong> when a seller selects{' '}
                          <strong>Electric Vehicle → {EV_SUBTYPES.find(s => s.value === modelForm.ev_subtype)?.label}</strong>.
                        </p>
                      </div>
                    )}

                    <Button onClick={handleSave} disabled={saving}
                      className="w-full bg-green-600 hover:bg-green-700 text-white"
                      data-testid="save-model-btn">
                      {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                      {editingId ? 'Update Model' : 'Create Model'}
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </div>
          </div>

          {/* Category tabs */}
          <div className="flex gap-2 flex-wrap">
            {Object.entries(CATEGORY_CONFIG).map(([key, cfg]) => {
              const count = brands.filter(b => b.category === key).length;
              return (
                <button key={key}
                  onClick={() => { setActiveCategory(key); setSearchQuery(''); }}
                  className={`px-4 py-1.5 rounded-full text-xs font-semibold border transition-all capitalize flex items-center gap-1.5 ${
                    activeCategory === key ? `${cfg.pill} border-current shadow-sm` : 'bg-white text-gray-500 border-gray-200 hover:border-gray-400 hover:text-gray-700'
                  }`}>
                  <span className={`w-1.5 h-1.5 rounded-full ${activeCategory === key ? cfg.dot : 'bg-gray-300'}`} />
                  {cfg.label}
                  {count > 0 && (
                    <span className={`text-xs rounded-full px-1.5 py-0.5 font-bold ${activeCategory === key ? 'bg-white/60' : 'bg-gray-100 text-gray-500'}`}>
                      {count}
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          {/* Search */}
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
            <Input value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
              placeholder={`Search by model or brand name in ${catCfg.label}...`}
              className="pl-9 pr-9 border-gray-200 focus:border-blue-400" />
            {searchQuery && (
              <button onClick={() => setSearchQuery('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>
      </CardHeader>

      <CardContent>
        {loading && displayModels.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 gap-3">
            <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
            <p className="text-sm text-gray-400">Loading {catCfg.label} models...</p>
          </div>

        ) : !loading && displayModels.length === 0 ? (
          <div className="text-center py-16 text-gray-400">
            <AlertCircle className="h-12 w-12 mx-auto mb-3 opacity-40" />
            <p className="font-medium">
              {searchQuery
                ? `No ${catCfg.label} models found matching "${searchQuery}"`
                : `No ${catCfg.label} models yet. Add your first!`}
            </p>
            {searchQuery && <button onClick={() => setSearchQuery('')} className="mt-2 text-sm text-blue-500 hover:underline">Clear search</button>}
          </div>

        ) : (
          <div className="space-y-5">
            {/* EV info banner */}
            {isEV && !loading && allModels.length > 0 && (
              <div className="flex items-start gap-3 bg-green-50 border border-green-200 rounded-xl px-4 py-3">
                <Zap className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-green-700">
                  EV models are tagged with a subtype (Car / Bike / Commercial). When a seller selects{' '}
                  <strong>Electric Vehicle → EV Car</strong>, only models tagged as <em>EV Car</em> appear,
                  and similarly for Bike and Commercial.
                </p>
              </div>
            )}

            {groupedByBrand.map(([brandId, brandModels]) => {
              const brand = getBrand(brandId);
              return (
                <div key={brandId} className="border border-gray-100 rounded-2xl overflow-hidden shadow-sm">
                  <div className="flex items-center gap-3 px-4 py-3 bg-gradient-to-r from-gray-50 to-white border-b border-gray-100">
                    {brand?.logo ? (
                      <img src={brand.logo} alt={brand.name} className="w-9 h-9 object-contain rounded-lg border border-gray-200 bg-white p-0.5 shadow-sm" />
                    ) : (
                      <div className="w-9 h-9 rounded-lg bg-blue-100 flex items-center justify-center text-blue-600 font-bold text-sm border border-blue-200">
                        {brand?.name?.charAt(0) || '?'}
                      </div>
                    )}
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      <h3 className="font-semibold text-gray-800 text-base truncate">{brand?.name || 'Unknown Brand'}</h3>
                      {brand?.category && (
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium capitalize flex-shrink-0 ${CATEGORY_CONFIG[brand.category]?.pill || 'bg-gray-100 text-gray-600'}`}>
                          {brand.category}
                        </span>
                      )}
                    </div>
                    <span className="text-xs text-gray-400 font-medium bg-gray-100 px-2 py-0.5 rounded-full flex-shrink-0">
                      {brandModels.length} model{brandModels.length !== 1 ? 's' : ''}
                    </span>
                  </div>

                  <div className="divide-y divide-gray-50">
                    {brandModels.map((model, idx) => {
                      const isNew = model.created_at &&
                        (Date.now() - new Date(model.created_at).getTime()) < 86400000;
                      const evSubCfg = isEV && model.ev_subtype
                        ? EV_SUBTYPES.find(s => s.value === model.ev_subtype)
                        : null;
                      return (
                        <div key={model.id}
                          className="flex items-center justify-between px-4 py-3 hover:bg-blue-50/40 transition-colors group"
                          data-testid={`model-row-${idx}`}>
                          <div className="flex items-center gap-3 min-w-0">
                            <span className="w-6 h-6 rounded-full bg-gray-100 text-gray-500 text-xs flex items-center justify-center font-medium flex-shrink-0 group-hover:bg-blue-100 group-hover:text-blue-600 transition-colors">
                              {idx + 1}
                            </span>
                            <div className="min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <p className="font-medium text-gray-800 text-sm truncate">{model.name}</p>
                                {isNew && (
                                  <span className="text-xs bg-green-100 text-green-700 px-1.5 py-0.5 rounded font-semibold flex-shrink-0">NEW</span>
                                )}
                                {/* EV subtype badge */}
                                {evSubCfg && (
                                  <span className="text-xs bg-green-100 text-green-700 border border-green-200 px-2 py-0.5 rounded-full font-medium flex items-center gap-1 flex-shrink-0">
                                    <span>{evSubCfg.icon}</span>{evSubCfg.label}
                                  </span>
                                )}
                                {/* Warn if EV model has no subtype */}
                                {isEV && !model.ev_subtype && (
                                  <span className="text-xs bg-amber-100 text-amber-700 border border-amber-200 px-2 py-0.5 rounded-full font-medium flex-shrink-0">
                                    ⚠️ No subtype
                                  </span>
                                )}
                              </div>
                              <p className="text-xs text-gray-400 mt-0.5">Added {fmtDate(model.created_at)}</p>
                            </div>
                          </div>
                          <div className="flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0 ml-3">
                            <Button variant="outline" size="sm"
                              className="h-8 px-3 text-xs border-gray-200 hover:border-blue-300 hover:text-blue-600"
                              onClick={() => openEdit(model)} data-testid={`edit-model-btn-${idx}`}>
                              <Edit className="h-3.5 w-3.5 mr-1" /> Edit
                            </Button>
                            <Button variant="outline" size="sm"
                              className="h-8 px-3 text-xs border-gray-200 hover:border-red-300 hover:text-red-600 hover:bg-red-50"
                              onClick={() => handleDelete(model.id)} data-testid={`delete-model-btn-${idx}`}>
                              <Trash2 className="h-3.5 w-3.5 mr-1" /> Delete
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}

            <div ref={sentinelRef} className="py-4 text-center">
              {loading && (
                <div className="flex items-center justify-center gap-2 text-gray-400">
                  <Loader2 className="h-5 w-5 animate-spin" />
                  <span className="text-sm">Loading more models...</span>
                </div>
              )}
              {!displayHasMore && displayModels.length > 0 && (
                <p className="text-xs text-gray-400 py-1">
                  ✓ All {displayTotal} {catCfg.label} model{displayTotal !== 1 ? 's' : ''} loaded
                </p>
              )}
              {displayHasMore && !loading && (
                <div className="flex items-center justify-center gap-1 text-gray-300">
                  <ChevronDown className="h-4 w-4 animate-bounce" />
                  <span className="text-xs">Scroll for more</span>
                </div>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default ModelsTab;